import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth, useIsAuthenticated } from './stores/auth';
import React, { useEffect, Suspense, useRef } from 'react';
import { useDeviceMode } from './hooks/useDeviceMode';

import { PageTitleProvider } from './contexts/PageTitleContext';
import ErrorBoundary from './components/ErrorBoundary';
import DynamicStyles from './components/Theme/DynamicStyles';
import InstallPrompt from './components/InstallPrompt';
import DesktopLayout from './components/DesktopLayout';
import PwaLayout from './components/PWA/PwaLayout';

import LandingPage from './pages/LandingPage';
import LandingPageV2 from './pages/LandingPageV2';
import B2bLandingPage from './pages/B2bLandingPage';
import StaticPageContent from './components/StaticPageContent';
import AboutUs from './pages/AboutUs';
import Contact from './pages/Contact';
import Login from './pages/Login';
import ItemList from './pages/ItemList';
import ItemDetailUnified from './pages/ItemDetailUnified';

const ImpresaPiuLanding = React.lazy(() => import('./pages/ImpresaPiuLanding'));

const Home = React.lazy(() => import('./pages/Home'));
const Trades = React.lazy(() => import('./pages/Trades'));
const TradeDetail = React.lazy(() => import('./pages/TradeDetail'));
const Dealers = React.lazy(() => import('./pages/Dealers'));
const DealerDetail = React.lazy(() => import('./pages/DealerDetail'));
const DealerUpdate = React.lazy(() => import('./pages/DealerUpdate'));
const DealerLocation = React.lazy(() => import('./pages/DealerLocation'));
const ShareView = React.lazy(() => import('./pages/ShareView'));
const Admin = React.lazy(() => import('./pages/Admin'));
const ImportData = React.lazy(() => import('./pages/ImportData'));
const UserManual = React.lazy(() => import('./pages/UserManual'));
const CaseStudyPage = React.lazy(() => import('./pages/CaseStudyPage'));
const DealerDashboard = React.lazy(() => import('./pages/DealerDashboard'));
const Register = React.lazy(() => import('./pages/Register'));
const TrackingStats = React.lazy(() => import('./pages/TrackingStats'));
const Settings = React.lazy(() => import('./pages/Settings'));
const DealerEditLocationPage = React.lazy(() => import('./pages/DealerEditLocationPage'));
const AddItem = React.lazy(() => import('./pages/AddItem'));
const MyItems = React.lazy(() => import('./pages/MyItems'));
const Map = React.lazy(() => import('./pages/Map'));
const MapExplorer = React.lazy(() => import('./pages/MapExplorer'));
const GeneralMap = React.lazy(() => import('./pages/GeneralMap'));
const PortfolioSection = React.lazy(() => import('./components/Portfolio/PortfolioSection'));
const BlogIndex = React.lazy(() => import('./components/Blog/BlogIndex'));

const Chat = React.lazy(() => import('./pages/Chat').then(m => ({ default: m.Chat })));
const BlogPost = React.lazy(() => import('./components/Blog/BlogIndex').then(m => ({ default: m.BlogPost })));
const MapExplorerDesktop = React.lazy(() => import('./pages/MapExplorer/index').then(m => ({ default: m.MapExplorerDesktop })));
const MapExplorerPwa = React.lazy(() => import('./pages/MapExplorer/index').then(m => ({ default: m.MapExplorerPwa })));

function RouteLoading() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#050507' }}>
      <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function LazyRoute({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<RouteLoading />}>{children}</Suspense>;
}

function AppPwaRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const path = location.pathname.replace(/^\/app/, '') || '/';
    navigate(path, { replace: true });
  }, [location.pathname, navigate]);
  return null;
}

function getPageType(pathname: string): string {
  if (pathname === '/' || pathname === '/home') return 'home';
  if (pathname === '/login') return 'login';
  if (pathname === '/register') return 'register';
  if (pathname === '/chat') return 'chat';
  if (pathname === '/admin' || pathname.startsWith('/admin')) return 'admin';
  if (pathname === '/settings') return 'settings';
  if (pathname === '/barche-usate-b2b') return 'b2b';
  if (pathname === '/barche-usate' || pathname.startsWith('/barche-usate/')) return 'consumer-seo';
  if (pathname === '/campagna/impresapiu') return 'campaign';
  if (pathname.includes('/boats/') || pathname.includes('/boat/')) return 'detail';
  if (pathname.includes('/motors/') || pathname.includes('/motor/')) return 'detail';
  if (pathname.includes('/accessories/') || pathname.includes('/accessory/')) return 'detail';
  if (pathname === '/boats' || pathname === '/motors' || pathname === '/accessories' || pathname === '/my-boats' || pathname === '/my-motors' || pathname === '/my-accessories') return 'list';
  return 'other';
}

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function GuestRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useIsAuthenticated();
  if (isAuthenticated) return <Navigate to="/boats" replace />;
  return <>{children}</>;
}

function AdminRoute({ children, fallback = "/dashboard" }: { children: React.ReactNode; fallback?: string }) {
  const isAuthenticated = useIsAuthenticated();
  const { user } = useAuth();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const isAdmin = user?.is_admin === true || user?.is_admin === '1' || (window as any).BOAT_TRADER_API?.is_admin === true;
  if (!isAdmin) return <Navigate to={fallback} replace />;
  return <>{children}</>;
}

function AppContent() {
  const { isInitialized } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;

  useEffect(() => {
    const currentPage = (window as any).BOAT_TRADER_CURRENT_PAGE;
    if (currentPage && currentPage !== 'home' && window.location.hash === '' && window.location.pathname === '/') {
      const path = '/' + currentPage;
      if (pathname !== path) {
        navigate(path, { replace: true });
      }
    }
  }, [navigate, pathname]);
  
  const { mode, isDesktop, isPWA, isMobileWeb } = useDeviceMode();
  
  useEffect(() => {
    try {
      document.body.classList.remove('desktop-mode', 'pwa-mode', 'mobile-web-mode');
      if (isDesktop) {
        document.body.classList.add('desktop-mode');
        document.documentElement.style.setProperty('--scroll-behavior', 'auto');
      } else if (isPWA) {
        document.body.classList.add('pwa-mode');
      } else if (isMobileWeb) {
        document.body.classList.add('mobile-web-mode');
      }
    } catch (e) {
      // silent
    }
  }, [mode, isDesktop, isPWA, isMobileWeb]);
  
  const ShowLayout = isDesktop ? DesktopLayout : PwaLayout;

  const updateBannerCleanup = useRef<() => void>(() => {});
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

    // When a new SW activates and takes control, reload to get fresh content
    const handleControllerChange = () => {
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);

    const check = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;
        reg.addEventListener('updatefound', () => {
          const sw = reg.installing || reg.waiting;
          if (sw) {
            if (sw.state === 'installed' && navigator.serviceWorker.controller) {
              showUpdateBanner(sw);
            } else {
              sw.addEventListener('statechange', () => {
                if (sw.state === 'installed' && navigator.serviceWorker.controller) {
                  showUpdateBanner(sw);
                }
              });
            }
          }
        });
        if (reg.waiting && navigator.serviceWorker.controller) {
          showUpdateBanner(reg.waiting);
        }
      } catch {}
    };
    check();
    const iv = setInterval(check, 300000);
    return () => {
      clearInterval(iv);
      navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      updateBannerCleanup.current();
    };
  }, []);

  function showUpdateBanner(sw: ServiceWorker) {
    try {
      updateBannerCleanup.current();
      const existing = document.getElementById('bt-sw-update-banner');
      if (existing) existing.remove();
      const banner = document.createElement('div');
      banner.id = 'bt-sw-update-banner';
      banner.innerHTML = [
        '<div style="position:fixed;bottom:96px;left:16px;right:16px;z-index:2147483647;background:rgba(255,251,235,0.95);-webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);border-radius:12px;border:1px solid rgba(253,230,138,0.5);padding:16px;display:flex;align-items:center;justify-content:space-between;gap:16px;font-family:system-ui,-apple-system,sans-serif;box-shadow:0 10px 25px rgba(0,0,0,0.1);">',
        '<div style="display:flex;align-items:center;gap:12px;">',
        '<div style="width:32px;height:32px;border-radius:8px;background:#fef3c7;display:flex;align-items:center;justify-content:center;">',
        '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>',
        '</div>',
        '<div>',
        '<p style="margin:0;font-weight:600;color:#92400e;font-size:14px;">Aggiornamento disponibile</p>',
        '<p style="margin:0;color:#b45309;font-size:12px;">Nuova versione pronta</p>',
        '</div></div>',
        '<div style="display:flex;align-items:center;gap:8px;">',
        '<button class="bt-sw-update-btn" style="padding:8px 16px;background:#d97706;color:white;border:none;border-radius:8px;font-size:13px;font-weight:600;cursor:pointer;">Aggiorna</button>',
        '<button class="bt-sw-close-btn" style="padding:0;width:32px;height:32px;background:transparent;border:none;cursor:pointer;color:#92400e;font-size:22px;display:flex;align-items:center;justify-content:center;">×</button>',
        '</div></div>'
      ].join('');
      if (!document.getElementById('bt-sw-anim-style')) {
        const style = document.createElement('style');
        style.id = 'bt-sw-anim-style';
        style.textContent = '@keyframes btSlideUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}.bt-sw-update-btn:hover{background:#b45309!important}';
        document.head.appendChild(style);
      }
      banner.style.animation = 'btSlideUp 0.3s ease-out';
      document.body.appendChild(banner);
      banner.querySelector('.bt-sw-update-btn')?.addEventListener('click', () => {
        sw.postMessage({ type: 'SKIP_WAITING' });
        window.location.reload();
      });
      banner.querySelector('.bt-sw-close-btn')?.addEventListener('click', () => banner.remove());
      updateBannerCleanup.current = () => { try { banner.remove(); } catch {} };
    } catch (e) {
      // silent
    }
  }

  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050507' }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
          <span style={{ color: 'rgba(255,255,255,0.5)', fontFamily: 'JetBrains Mono', fontSize: '13px' }}>Loading Sea Level Traders...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/app/*" element={<AppPwaRedirect />} />
        <Route path="/share/:token" element={<LazyRoute><ShareView /></LazyRoute>} />
        <Route path="/test-share" element={<div>Test share route works!</div>} />
        <Route path="/home" element={<GuestRoute><LandingPage /></GuestRoute>} />
        <Route path="/landing" element={<GuestRoute><LandingPage /></GuestRoute>} />
        <Route path="/landing-v2" element={<GuestRoute><LandingPageV2 /></GuestRoute>} />
        <Route path="/campagna/impresapiu" element={<LazyRoute><ImpresaPiuLanding /></LazyRoute>} />
        <Route path="/manuale" element={<LazyRoute><UserManual /></LazyRoute>} />
        <Route path="/guida" element={<LazyRoute><UserManual /></LazyRoute>} />
        <Route path="/" element={<ShowLayout />}>
          <Route path="portfolio" element={<LazyRoute><PortfolioSection /></LazyRoute>} />
          <Route path="portfolio/:slug" element={<LazyRoute><CaseStudyPage /></LazyRoute>} />
          <Route path="blog" element={<LazyRoute><BlogIndex /></LazyRoute>} />
          <Route path="blog/:slug" element={<LazyRoute><BlogPost /></LazyRoute>} />
          <Route path="barche-usate-b2b" element={<B2bLandingPage />} />
          <Route path="consignment-barche" element={<StaticPageContent />} />
          <Route path="compro-barca-usata" element={<StaticPageContent />} />
          <Route path="diventa-dealer" element={<StaticPageContent />} />
          <Route path="barche-usate-lombardia-b2b" element={<StaticPageContent />} />
          <Route path="barche-usate-puglia-b2b" element={<StaticPageContent />} />
          <Route path="barche-usate-toscana-b2b" element={<StaticPageContent />} />
          <Route path="barche-usate-sicilia-b2b" element={<StaticPageContent />} />
          <Route path="barche-usate-lazio-b2b" element={<StaticPageContent />} />
          <Route path="barche-usate-piemonte-b2b" element={<StaticPageContent />} />
          <Route path="barche-usate" element={<StaticPageContent />} />
          <Route path="barche-usate/:region" element={<StaticPageContent />} />
          <Route path="chi-siamo" element={<AboutUs />} />
          <Route path="contatti" element={<Contact />} />
          <Route path="catalogo" element={<Navigate to="/boats" />} />
          <Route index element={<GuestRoute><LandingPage /></GuestRoute>} />
          <Route path="map" element={<LazyRoute><Map /></LazyRoute>} />
          <Route path="mapboats" element={<LazyRoute><Map /></LazyRoute>} />
          <Route path="map-explorer" element={<LazyRoute><MapExplorer /></LazyRoute>} />
          <Route path="map-explorer-desktop" element={<LazyRoute><MapExplorerDesktop /></LazyRoute>} />
          <Route path="map-explorer-pwa" element={<LazyRoute><MapExplorerPwa /></LazyRoute>} />
          <Route path="general-map" element={<LazyRoute><GeneralMap /></LazyRoute>} />
          <Route path="boats" element={<LazyRoute><ItemList itemType="boats" /></LazyRoute>} />
          <Route path="motors" element={<LazyRoute><ItemList itemType="motors" /></LazyRoute>} />
          <Route path="boats/:id" element={<LazyRoute><ItemDetailUnified itemType="boats" /></LazyRoute>} />
          <Route path="dealers" element={<PrivateRoute><LazyRoute><Dealers /></LazyRoute></PrivateRoute>} />
          <Route path="dealers/:id" element={<PrivateRoute><LazyRoute><DealerDetail /></LazyRoute></PrivateRoute>} />
          <Route path="dealers/:id/edit" element={<PrivateRoute><LazyRoute><DealerUpdate /></LazyRoute></PrivateRoute>} />
          <Route path="dealers/:id/location" element={<PrivateRoute><LazyRoute><DealerLocation /></LazyRoute></PrivateRoute>} />
          <Route path="dealer/:id" element={<PrivateRoute><LazyRoute><DealerDetail /></LazyRoute></PrivateRoute>} />
          <Route path="login" element={<Login />} />
          <Route path="register" element={<GuestRoute><LazyRoute><Register /></LazyRoute></GuestRoute>} />
          <Route path="my-boats" element={<PrivateRoute><LazyRoute><MyItems itemType="boats" /></LazyRoute></PrivateRoute>} />
          <Route path="add-boat" element={<PrivateRoute><LazyRoute><AddItem itemType="boats" /></LazyRoute></PrivateRoute>} />
          <Route path="add/boats" element={<PrivateRoute><LazyRoute><AddItem itemType="boats" /></LazyRoute></PrivateRoute>} />
          <Route path="edit-boat/:id" element={<PrivateRoute><LazyRoute><AddItem itemType="boats" /></LazyRoute></PrivateRoute>} />
          <Route path="edit-boats/:id" element={<PrivateRoute><LazyRoute><AddItem itemType="boats" /></LazyRoute></PrivateRoute>} />
          <Route path="my-motors" element={<PrivateRoute><LazyRoute><MyItems itemType="motors" /></LazyRoute></PrivateRoute>} />
          <Route path="add-motor" element={<PrivateRoute><LazyRoute><AddItem itemType="motors" /></LazyRoute></PrivateRoute>} />
          <Route path="add/motors" element={<PrivateRoute><LazyRoute><AddItem itemType="motors" /></LazyRoute></PrivateRoute>} />
          <Route path="edit-motor/:id" element={<PrivateRoute><LazyRoute><AddItem itemType="motors" /></LazyRoute></PrivateRoute>} />
          <Route path="edit-motors/:id" element={<PrivateRoute><LazyRoute><AddItem itemType="motors" /></LazyRoute></PrivateRoute>} />
          <Route path="motor/:id" element={<LazyRoute><ItemDetailUnified itemType="motors" /></LazyRoute>} />
          <Route path="motors/:id" element={<LazyRoute><ItemDetailUnified itemType="motors" /></LazyRoute>} />
          <Route path="trades" element={<PrivateRoute><LazyRoute><Trades /></LazyRoute></PrivateRoute>} />
          <Route path="accessories" element={<LazyRoute><ItemList itemType="accessories" /></LazyRoute>} />
          <Route path="accessories/:id" element={<LazyRoute><ItemDetailUnified itemType="accessories" /></LazyRoute>} />
          <Route path="my-accessories" element={<PrivateRoute><LazyRoute><MyItems itemType="accessories" /></LazyRoute></PrivateRoute>} />
          <Route path="add-accessory" element={<PrivateRoute><LazyRoute><AddItem itemType="accessories" /></LazyRoute></PrivateRoute>} />
          <Route path="add/accessories" element={<PrivateRoute><LazyRoute><AddItem itemType="accessories" /></LazyRoute></PrivateRoute>} />
          <Route path="edit-accessory/:id" element={<PrivateRoute><LazyRoute><AddItem itemType="accessories" /></LazyRoute></PrivateRoute>} />
          <Route path="edit-accessories/:id" element={<PrivateRoute><LazyRoute><AddItem itemType="accessories" /></LazyRoute></PrivateRoute>} />
          <Route path="admin/edit-accessory/:id" element={<AdminRoute fallback="/login"><LazyRoute><AddItem itemType="accessories" adminMode={true} adminReturnPath="/admin?tab=accessories" /></LazyRoute></AdminRoute>} />
          <Route path="trades/:id" element={<PrivateRoute><LazyRoute><TradeDetail /></LazyRoute></PrivateRoute>} />
          <Route path="quote-requests" element={<PrivateRoute><LazyRoute><TrackingStats /></LazyRoute></PrivateRoute>} />
          <Route path="chat" element={<PrivateRoute><LazyRoute><Chat /></LazyRoute></PrivateRoute>} />
          <Route path="dashboard" element={<PrivateRoute><LazyRoute><DealerDashboard /></LazyRoute></PrivateRoute>} />
          <Route path="admin" element={<AdminRoute fallback="/dashboard"><LazyRoute><Admin /></LazyRoute></AdminRoute>} />
          <Route path="admin/edit-boat/:id" element={<AdminRoute fallback="/login"><LazyRoute><AddItem itemType="boats" adminMode={true} adminReturnPath="/admin?tab=boats" /></LazyRoute></AdminRoute>} />
          <Route path="admin/edit-motor/:id" element={<AdminRoute fallback="/login"><LazyRoute><AddItem itemType="motors" adminMode={true} adminReturnPath="/admin?tab=motors" /></LazyRoute></AdminRoute>} />
          <Route path="import-data" element={<AdminRoute fallback="/"><LazyRoute><ImportData /></LazyRoute></AdminRoute>} />
          <Route path="tracking-stats" element={<AdminRoute fallback="/dashboard"><LazyRoute><TrackingStats /></LazyRoute></AdminRoute>} />
          <Route path="settings" element={<PrivateRoute><LazyRoute><Settings /></LazyRoute></PrivateRoute>} />
          <Route path="edit-location" element={<PrivateRoute><LazyRoute><DealerEditLocationPage /></LazyRoute></PrivateRoute>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <DynamicStyles />
        <InstallPrompt />
        <PageTitleProvider>
          <AppContent />
        </PageTitleProvider>
      </ErrorBoundary>
    </AuthProvider>
  );
}
